import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IntakeDegradationReason } from '../intake.api';

export interface DegradationBannerProps {
  reason: IntakeDegradationReason;
}

/**
 * h5-6 FR34: degradation is never silent, but it is never alarming either — DESIGN.md mandates
 * an info-blue banner (never red/amber), since AI unavailability or hitting the cost ceiling is
 * never the customer's fault. The banner may collapse, but the status itself stays visible
 * (collapsing only hides the explanatory copy, not the fact that it's degraded).
 */
export function DegradationBanner({ reason }: DegradationBannerProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      role="status"
      data-testid="intake-degradation-banner"
      className="rounded-[var(--radius-card)] border border-[var(--info)] bg-[var(--info-light)] p-3 text-[var(--info-text)]"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium">{t('intake.review.degradation.title')}</p>
        <button
          type="button"
          data-testid="intake-degradation-banner-toggle"
          onClick={() => setCollapsed((value) => !value)}
          className="text-xs underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
        >
          {collapsed ? t('intake.review.degradation.expand') : t('intake.review.degradation.collapse')}
        </button>
      </div>
      {!collapsed ? (
        <p className="mt-1 text-sm" data-testid="intake-degradation-banner-copy">
          {t(`intake.review.degradation.reasons.${reason}`)}
        </p>
      ) : null}
    </div>
  );
}
